import React, { useEffect, useState, createContext, useContext } from "react";
import { makeStyles, Theme, createStyles } from "@mui/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem, Paper, Typography } from "@mui/material";
import { isEqual, debounce } from "lodash";
import DefinitionSection from "~common/form/definition-section-fc";
import ErrorView from "~common/components/error-view";
import SuccessView from "~common/components/success-view";
import { DataContext } from "~common/form/icg-definition-data-provider";
import LoadingView from "~common/components/loading-view";
import { getValueAtPath } from "~common/form/helper";

export interface FocusItemContextProps {
  focusItem: any;
  setFocusItem: (attachment: any) => void;
}

export const FocusItemContext = createContext();


let selectionColor = "orange";
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    rowEven: {
      
    },
    rowOdd: {
      backgroundColor: "#ededed"
    },
    container: {
      width: "100%",
      display: "flex",
    },
    row: {
      width: "100%",
    },
    clickableRow: {
      width: '100%',
      cursor: 'pointer'
    },
    clickableMenu: {
      cursor: 'pointer',
      marginRight: 15,
      minWidth: 48,
      minHeight: 48
    },
    selectorArea: {
      width: 15,
      position: 'relative'
    },
    selectedRow: {
      border: '1px solid orange' //TODO:  Make this match the primary button color or another color from the theme, so that it is easily configurable.
    },
    unselectedRow: {
      border: '0px'
    },
    selectorWithError: {
      color: 'red',
      position: 'absolute',
      left: '0px',
      top: '0px'
    },
    selector: {
      color:
        theme.overrides &&
        theme.overrides.MuiButton &&
        theme.overrides.MuiButton.raisedPrimary &&
        theme.overrides.MuiButton.raisedPrimary.backgroundColor
          ? theme.overrides.MuiButton.raisedPrimary.backgroundColor
          : selectionColor,
      position: "absolute",
      left: "0px",
      top: "0px",
    },
    hover: {
      "&:hover": {
        backgroundColor: "#dcdbdc",
        border: "solid #b8b8b8 1px",
      },
      border: "solid #ffffff 1px",
    },
    listFunctionLine: {
      display: "flex",
      justifyContent: "space-between",
    },
    addNewRow: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    addNewRowNoResults: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: 15,
    },
    listTitle: {
      marginBottom: 10,
    },
    sectionTitle: {
      // fontSize: "0.9rem",
      marginBottom: 10,
      fontSize: "1.3125rem",
      fontWeight: 500,
      fontFamily: "Roboto,Arial,sans-serif",
      lineHeight: "1.16667em",
      paddingLeft: 8,
    },
    listFunctions: {
      display: "flex",
      justifyContent: "flex-end",
    },
    listFunctionButton: {
      // color: "#8d8d8d",
      fontSize: "1.0rem",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    listFunctionButtonIcon: {
      minWidth: "1.0rem",
    },
    resultsSection: {
      width: "100%",
      padding: 10,
      boxShadow: "0px 0px 0px #FFFFFF;none",
    },
    paper: {
      backgroundColor: "#FFFFFF",
      boxShadow: "0px 0px 0px #FFFFFF;none",
    },
    summaries: {
      backgroundColor: "#FFFFFF",
      boxShadow: "0px 0px 0px #FFFFFF;none",
      padding: theme.spacing(4),
    },
    noResultsSection: {
      padding: 30,
      paddingBottom: 15,
      boxShadow: "0px 0px 0px #FFFFFF;none",
      display: "flex",
      justifyContent: "center",
    },
    // An alternate to the above used when returning multiples
    resultsListSection: {
      padding: 10,
      marginTop: 15,
      boxShadow: "0px 0px 0px #FFFFFF;none",
    },
    pagerArea: {
      display: "flex",
      flexDirection: "row-reverse",
    },
    pager: {},
    evenRow: {
      backgroundColor: "#fff",
      border: "1px solid rgba(255,255,255,0.0)",
      padding: "10px 5px 5px 5px",
      margin: 1,
    },
    oddRow: {
      backgroundColor: "#f0f0f0",
      border: "1px solid rgba(255,255,255,0.0)",
      padding: "10px 5px 5px 5px",
      margin: 1,
    },
    buttonBar: {
      display: "flex",
      justifyContent: "space-between",
      spaceBetween: 15,
      padding: 15,
      paddingBottom: 0,
      paddingTop: 0,
    },
    addBar: {
      display: "flex",
      justifyContent: "flex-start",
      paddingBottom: 0,
    },
    sortArea: {
      justifyContent: "flex-start",
      alignItems: "center",
      minHeight: 50,
      minWidth: 150,
    },
    sortOptions: {
      maxWidth: 300,
      minWidth: 150,
      marginRight: 30,
      width: "100%",
    },
    sortOptionIcon: {
      marginLeft: 5,
    },
    buttonArea: {
      marginTop: 15,
      justifyContent: "flex-end",
      minHeight: 50,
      minWidth: 180,
    },
    buttonSubmit: {
      marginLeft: "15px !important",
    },
    buttonAddNew: {
      marginLeft: 15,
      marginRight: 15,
      justifyContent: "flex-end",
    },
    buttonCancel: {
      paddingLeft: 15,
    },
    noResultsText: {
      display: "flex",
      justifyContent: "center",
      paddingLeft: 15,
    },
    simulationWarning: {
      border: "1px solid #fdde00",
      backgroundColor: "#fffedc",
      padding: 15,
      marginBottom: 15,
      textAlign: "center",
    },
    summaryActionPolicyMessages: {
      border: "2px solid red",
      margin: 1,
    },
    selectionActionArea: {
      display: "flex",
      justifyContent: "space-between",
      textAlign: "right",
      paddingLeft: 6,
      paddingRight: 5,
      paddingBottom: 5,
      verticalAlign: "baseline",
      minHeight: 45,
      marginBottom: 5,
    },
    selectionCount: {
      display: "inline-block",
      width: "40%",
      textAlign: "center",
      paddingTop: 10,
    },
    selectionAction: {
      display: "inline-block",
      width: "30%",
    },
    allSelectors: {
      display: "inline-block",
      width: "30%",
      color: "#333",
      textAlign: "left",
      paddingTop: 10,
    },
    select: {
      color:
        theme.overrides &&
        theme.overrides.MuiButton &&
        theme.overrides.MuiButton.raisedPrimary &&
        theme.overrides.MuiButton.raisedPrimary.backgroundColor
          ? theme.overrides.MuiButton.raisedPrimary.backgroundColor
          : selectionColor,
      display: "inline-block",
      cursor: "pointer",
    },
    unselect: {
      color:
        theme.overrides &&
        theme.overrides.MuiButton &&
        theme.overrides.MuiButton.raisedPrimary &&
        theme.overrides.MuiButton.raisedPrimary.backgroundColor
          ? theme.overrides.MuiButton.raisedPrimary.backgroundColor
          : selectionColor,
      display: "inline-block",
      marginLeft: 15,
      cursor: "pointer",
    },
    summaryActionMessage: {
      fontWeight: 600,
      paddingBottom: 10,
      color:
        theme.overrides &&
        theme.overrides.MuiButton &&
        theme.overrides.MuiButton.raisedPrimary &&
        theme.overrides.MuiButton.raisedPrimary.backgroundColor
          ? theme.overrides.MuiButton.raisedPrimary.backgroundColor
          : selectionColor,
      fontStyle: "italic",
    },
  })
);

interface QueryItemViewProps {
  allData: any;
  itemData: any;

  resultItemDef: any;
  onRowClick?: Function;
  actionsForItemRow?: Function;
  done: Function;
  reserveRowActionSpace: boolean;
  customRenderers?: any;
  customMessages?: any;
  noResultsText?: string;
  hasExecutedQuery?: boolean;
  getNoResultsText?: any;
  actionsForSelectedItems?: Function;
  className?: string;
  policyMessages?: Array<any>;
  gridSpacing?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  // the below are used for selection
  onSelectionChange?: Function;
  resultsTitleOverride?: string;
  selected: boolean;
  index: number;
  criteria: any;
  currentStatus: string;
  suppressExport:boolean;
}
var iterator: any;
const ITEM_HEIGHT = 48;
export const QueryResultsView: React.FC<QueryItemViewProps> = ({
  allData,
  currentStatus,
  itemData,
  done,
  resultItemDef,
  onRowClick,
  noResultsText,
  getNoResultsText,
  hasExecutedQuery,
  actionsForItemRow,
  actionsForSelectedItems,
  reserveRowActionSpace,
  customRenderers,
  customMessages,
  className,
  policyMessages,
  gridSpacing,
  // the below are used for selection
  onSelectionChange,
  resultsTitleOverride,
  selected,
  index,
  error, 
  success,
  criteria,
  suppressExport,
}) => {
  const [fullData, setFullData] = useContext(DataContext);
  const [focusItem, setFocusItem] = useState(null)
  const classes = useStyles();


  var noResText;
  if (getNoResultsText) {
    noResText = getNoResultsText();
  }
  if (!noResText) {
    noResText = noResultsText || "No Results Found";
  }

  var resultsSection: any = null;

  var resultList = ((fullData || {}).results || []).map(
    (r: any, idx: any) => {

      r["_idx"] = idx;

      var classNames: Array<string> = [`query-list-item-${idx}`];

      if (idx % 2 == 0) {
        classNames.push(classes.evenRow);
      } else {
        classNames.push(classes.oddRow);
      }


      return (
        <div key={idx} className={classNames.join(" ")}>
          <FocusItemContext.Provider value={[focusItem, setFocusItem]}>
          <div style={r.__style__ || {}}>
            <QueryItemRow
              key={`row_${idx}`}
              itemData={r}
              resultItemDef={resultItemDef}
              onRowClick={(r.__attrs__ || {clickable: true}).clickable ? onRowClick : ()=>{}}
              actionsForItemRow={actionsForItemRow}
              actionsForSelectedItems={actionsForSelectedItems}
              customRenderers={customRenderers}
              reserveRowActionSpace={reserveRowActionSpace}
              onSelectionChange={onSelectionChange}
              index={idx}
            />
          </div>
          </FocusItemContext.Provider>
        </div>
        
      );
    }
  );

  if (
    ((fullData || {}).results || []).length < 1 
  ) {
    resultList = (
      <div name="QueryListNoResultsMessage" className={classes.noResultsSection + " "}>
      <div
        dangerouslySetInnerHTML={{ __html: hasExecutedQuery ? noResText : "" }}
        className={classes.noResultsText + " " + classes.paper}
      ></div>
      <div className={classes.listFunctionLine}></div>
    </div>

    );
   }

  // if (((fullData || {}).results || []).length > 0) {
  //   // done("ready")
  // }
  if (currentStatus != "ready" && currentStatus != "paging") {
    return <LoadingView />;
  } else {
    return (
      <div>
        {resultList}
      </div>
      
    )
  }
};

const QueryItemRow: React.FC<QueryItemViewProps> = ({
  allData,
  itemData,
  resultItemDef,
  onRowClick,
  actionsForItemRow,
  reserveRowActionSpace,
  customRenderers,
  customMessages,
  className,
  policyMessages,
  gridSpacing,
  onSelectionChange,
  // the below are used for selection
  actionsForSelectedItems,
  resultsTitleOverride,
  // selected,
  index,
  criteria,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [fullData, setFullData] = useContext(DataContext)// as DataContextType;
  // Carried over from Class component though it appears to not be in use
  const [selected, setSelected] = useState(false);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRowClick = () => {
    if (onRowClick) {
      onRowClick(getValueAtPath(`/results/${index}`, fullData));
    }
  };

  const handleActionClick = (action: any) => () => {
    handleMenuClose();
    if (action.onClick) {
      action.onClick(itemData);
    }
  };



  useEffect(() => {
  // Ensure Selected
    if (fullData && fullData.results && fullData.results[index]) {
      if (fullData.results[index]["_selected"] && !selected) {
        setSelected(true)
      } else if (!fullData.results[index]["_selected"] && selected)  {
        setSelected(false)
      }
    }
  })
  var actionArea;
  const open = Boolean(anchorEl);

  if (actionsForItemRow) {
    const actions = actionsForItemRow(itemData || {}, criteria);
    if (actions && actions.length > 0) {
      actionArea = (
        <div
          className={classes.clickableMenu + " " + "query-item-view-actions"}
        >
          <IconButton
            size="large"
            aria-label="More"
            aria-owns={open ? "actionMenu" : undefined}
            aria-haspopup="true"
            onClick={handleMenuClick}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="actionMenu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            PaperProps={{
              style: {
                maxHeight: ITEM_HEIGHT * 4.5,
              },
            }}
          >
            {actions.map((action: any, idx: number) => {
              return (
                <MenuItem key={idx} onClick={handleActionClick(action)}>
                  <span dangerouslySetInnerHTML={{ __html: action.label }} />
                </MenuItem>
              );
            })}
          </Menu>
        </div>
      );
    } else {
      if (reserveRowActionSpace) {
        actionArea = (
          <div
            className={classes.clickableMenu + " " + "query-item-view-actions"}
          >
            <IconButton size="large"></IconButton>
          </div>
        );
      }
    }
  }

  let rowClass = classes.row;
  let rowOdd = classes.rowOdd;
  let rowEven = classes.rowEven; 

  if (onRowClick) {
    rowClass = classes.clickableRow;
  }

  let selectorIcon: string = "far fa-square ";
  let selectionClass: string = " unselectedRow ";
  if (selected) {
    selectorIcon = "far fa-check-square ";
    selectionClass = " selectedRow ";
  }

  let selectionArea: any = null;
  
  if (actionsForSelectedItems) {
    if (itemData && itemData["_SummaryActionPolicyMessages"]) {
      selectionArea = (
        <div className={classes.summaryActionPolicyMessages}>
          <i
            aria-label="Select Item Toggle"
            className={selectorIcon + classes.selectorWithError}
            onClick={onSelectionChange(index)}
          />
        </div>
      );
    } else {
      selectionArea = (
        <div className={classes.selectorArea}>
          <i
            aria-label="Select Item Toggle"
            className={selectorIcon + classes.selector}
            onClick={onSelectionChange(index)}
          />
        </div>
      );
    }
  }

  return (
    <div
      className={
        "query-item-view " +
        classes.container +
        " " +
        `${"query-item-view"}${index}`
      }
    >
      {selectionArea}
      <div onClick={handleRowClick} className={rowClass + selectionClass + (index % 2 == 0 ? rowEven : rowOdd)}>
        <DefinitionSection
          definition={resultItemDef}
          path={`/results/${index}`}
          customRenderers={customRenderers}
          // path={''}
          policyMessages={policyMessages}
          suppressTitle={true}
        />
      
      </div>
      {actionArea}
      {
        // Putting this at the bottom of the sibling HTML nodes so that is naturally orders to the top, although, we can also use a z-index if necessary.
      }
    </div>
  );
};

export default QueryResultsView;
